import { useState, useCallback } from 'react';

interface UsePickingActionModalsProps {
  executePickingBulkAction: (action: 'void' | 'group') => Promise<void>;
  isVoiding: boolean;
  isGrouping: boolean;
}

interface UsePickingActionModalsReturn {
  isVoidModalOpen: boolean;
  openVoidModal: () => void;
  closeVoidModal: () => void;
  handleConfirmVoid: () => Promise<void>;
  isGroupModalOpen: boolean;
  openGroupModal: () => void;
  closeGroupModal: () => void;
  handleConfirmGroup: () => Promise<void>;
}

export const usePickingActionModals = ({
  executePickingBulkAction, // isVoiding and isGrouping are destructured but not used directly in this hook's logic
}: // Their primary use is for prop validation and to be available if needed in future
UsePickingActionModalsProps): UsePickingActionModalsReturn => {
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);

  const openVoidModal = useCallback(() => setIsVoidModalOpen(true), []);
  const closeVoidModal = useCallback(() => setIsVoidModalOpen(false), []);

  const openGroupModal = useCallback(() => setIsGroupModalOpen(true), []);
  const closeGroupModal = useCallback(() => setIsGroupModalOpen(false), []);

  const handleConfirmVoid = useCallback(async () => {
    try {
      await executePickingBulkAction('void');
    } catch (err) {
      console.error('Failed to void picks:', err);
      // Optionally, re-throw or handle error state for UI feedback if needed beyond console logging.
    } finally {
      closeVoidModal();
    }
  }, [executePickingBulkAction, closeVoidModal]);

  const handleConfirmGroup = useCallback(async () => {
    try {
      await executePickingBulkAction('group');
    } catch (err) {
      console.error('Failed to group picks:', err);
      // Optionally, re-throw or handle error state for UI feedback if needed beyond console logging.
    } finally {
      closeGroupModal();
    }
  }, [executePickingBulkAction, closeGroupModal]);

  return {
    isVoidModalOpen,
    openVoidModal,
    closeVoidModal,
    handleConfirmVoid,
    isGroupModalOpen,
    openGroupModal,
    closeGroupModal,
    handleConfirmGroup,
  };
};
